---
slug: "the-front-door-your-contact-center-is-the-first-step-in-the-patient-journey"
title: "The Front Door: Your Contact Center Is the First Step in the Patient Journey"
author: "Greg Hatch"
description: "Patients form their opinion of your hospital before they ever see a clinician. Greg Hatch on why the contact center is healthcare's real front door, and what it costs when it fails."
image: "/img/blog-images/hospital-front-door.webp"
---

<Section data-accent>
Ask patients what they think of your hospital and most will start with a phone call, not the lobby or the exam room.

By Greg Hatch, SVP of Strategic Growth
</Section>

<Section>
I spend a lot of time asking healthcare leaders where communication breaks down in the patient journey. The most honest answer I ever got was "where doesn't it?"

Setting an appointment is harder than it should be. The visit itself carries friction the patient feels. And when a medical issue requires several providers to work together, that collaboration is not guaranteed. Communication touches every step of the patient journey, and the first touch is almost always the contact center.

We file that contact center under overhead, which is the mistake. The contact center is the front door of the hospital, and it shapes how every patient feels about everything that comes after.
</Section>

<Section>
## What We Ask Patients to Survive

Before a patient ever receives care, we put them through a test. Call the main line. Work through a menu that mirrors our org chart instead of their problem. Wait on hold. Get transferred. Explain the situation again, because the context didn't follow them.

Now run that gauntlet at 78 years old, with four chronic conditions and a question about a medication you can't miss. The patients with the highest need for care get the least usable version of our front door. Many of them don't embrace the self-service tools that could help, so the phone is what they have, and the phone is where we lose them.

The front door is where trust starts, and ours is often locked.
</Section>

<Section>
## The Numbers Behind the Front Door

The peer-reviewed picture is rough. A study using 2015-2016 VHA data across 285 medical facilities associated shorter telephone waits with better patient perceptions of urgent-care access. It did not establish that call delays caused delayed treatment.

And when the federal government scores Medicare plan sponsors, it measures their phone channel directly: hold times under two minutes, abandonment under 5%.

Long waits, transfers and unresolved calls can make access harder. Track your own answer speed, abandonment, repeat contacts and first-contact resolution to identify where patients encounter friction.

When regulators treat your hold music as a quality measure, the front door is officially part of care delivery. It always was.
</Section>

<Section>
## Why Hospitals Keep Losing This

Most hospitals measure the front door with one question: did the call get answered? That cost-center metric tracks the door, not what happens to the patient after they walk through it.

The contact center usually runs on systems that know nothing about the patient. The agent who answers can't see that this caller had an appointment last Tuesday, a billing question last month, and a referral in progress. So the patient repeats their story, and the agent transfers them.

Sometimes it's worse than a blind spot. Some organizations freeze the whole channel because of privacy fear. HIPAA says be careful, so they allow nothing. That's how compliance becomes the enemy of care instead of its guardrail. The balance is achievable, but only when IT, risk, and clinical leadership work on it together instead of in isolation.
</Section>

<Section>
## What a Connected Front Door Looks Like

This is where Gage and Avaya come in together.

A connected front door remembers. With configured integrations and authorized access, Avaya Infinity can pull voice, text, web, and chat into one continuous timeline, so the patient's story can travel with them. The scheduler can see yesterday's bill question and last month's referral instead of asking the caller to repeat their story.

Automation takes the routine work: refill requests, directions, FAQs, the scheduling that can be self-served at 10pm when the patient finally has time to think about it. That clears the line for the calls that need a human being, and frees agents to be present in the moments that matter.

That's what we mean by AI automation with empathy. The technology handles the background so people can handle each other.

And it makes the front door measurable in the way that counts: how fast we answered, and how the patient felt about the entire experience, from the first ring to the resolved question.
</Section>

<Section>
## Start by Calling Yourself

If you lead a health system, try this. Tomorrow at 8am, before your first meeting, call your own main line as if you were a patient with a problem. Count the menus, time the hold, and notice how much you have to repeat.

That call is your patient's first experience of your organization, long before anyone in a white coat says hello. The organizations that treat the front door as the first step in the patient journey, and measure it with the same seriousness as clinical outcomes, are the ones patients choose and stay with.

The front door is the first act of care, and it's time we built it that way.
</Section>

<br />
<br />

<CTASection imageSrc="/img/section-images/healthcare-hero.webp">
  ## Watch the Avaya Healthcare Video Series

  See Greg Hatch explore how healthcare leaders can connect communication, collaboration, AI, and compliance into a better patient journey, from first touch through discharge and beyond.

  <CTAButtons>
    <ButtonCTA href="/avaya-healthcare-series">Watch the Series</ButtonCTA>
  </CTAButtons>
</CTASection>

<Section>
<div class="ai-sources">
  <div class="ai-sources-title">Sources and Citations</div>
  <ul class="ai-sources-list">
    <li>VHA study (2015-2016 data, 285 medical facilities, telephone waits and perceived urgent-care access) <a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC8177735/" target="_blank" rel="noopener noreferrer">pmc.ncbi.nlm.nih.gov</a></li>
    <li>CMS phone standards for Medicare plan sponsors (hold times, abandonment) <a href="https://www.dialoghealth.com/post/healthcare-call-center-statistics" target="_blank" rel="noopener noreferrer">dialoghealth.com</a></li>
  </ul>
</div>
</Section>
